import { useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { toast } from "react-toastify";

import Table from "../shared/components/Table";
import useTable from "../shared/hooks/useTable";
import Badge from "../shared/components/Badge";
import useCheckboxColumn from "../shared/components/Table/UseCheckboxColumn";
import useActionsColumn from "../shared/components/Table/UseActionsColumn";
import useDateColumn from "../shared/hooks/useDateColumn";
import "../shared/style/typography.scss";

import Heading from "./Typography/Heading";
import UpdateBulkGeneratorDialog from "./Dialogs/UpdateBulkGeneratorDialog";

const columnHelper = createColumnHelper();

/**
 * @param {object} props
 * @param {object[]} props.biulkLinks
 */
function BulkLinks({ bulkLinks, setBulkLinks, onDownload, onDelete }) {
    const [isUpdateBulkLinkDialogOpen, setIsUpdateBulkLinkDialogOpen] = useState();
    const [bulkUploadToUpdate, setBulkUploadToUpdate] = useState();

    /** @type {[import("@tanstack/react-table").RowSelectionState, React.Dispatch<React.SetStateAction<import("@tanstack/react-table").RowSelectionState>>]} */
    const [rowSelection, setRowSelection] = useState({});
    const checkboxColumn = useCheckboxColumn();
    const createdAtColumn = useDateColumn({
        id: "created_at",
        header: "Date",
        width: "20%",
    });

    const actionsColumn = useActionsColumn(
        [
            {
                label: "Download CSV",
                onClick: (row) => onDownload(row.id),
            },
            {
                label: "Edit",
                onClick: (row) => onEdit(row),
            },
            {
                label: "Delete",
                onClick: (row) => onDelete(row.id),
            },
        ],
        "7.5%",
    );

    const table = useTable({
        data: useMemo(
            () =>
                (bulkLinks?.map ? bulkLinks : []).map((row) => ({
                    ...row,
                })),
            [bulkLinks],
        ),
        columns: useMemo(
            () => [
                columnHelper.accessor("name", {
                    header: "Name",
                    width: "25%",
                    cell: (info) => (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span className="paragraph-4-semibold" style={{ color: "var(--colors-primary-slate-500)" }}>
                                {info?.getValue()}
                            </span>
                            <div
                                className="paragraph-6-regular"
                                style={{
                                    color: "var(--colors-primary-slate-500)",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {info?.row.original.link_name}
                            </div>
                        </div>
                    ),
                }),
                columnHelper.accessor("respondent_count", {
                    header: "Responses",
                    width: "10%",
                    cell: (info) => `${info?.row.original.response_count}/${info?.getValue()}`,
                }),
                createdAtColumn,
                columnHelper.accessor("is_active", {
                    header: "Status",
                    width: "10%",
                    cell: (info) =>
                        info?.getValue() ? (
                            <Badge text="Open" className="badge-success badge-filled badge-small" leftIcon={false} />
                        ) : (
                            <Badge text="Closed" className="badge-neutral badge-filled badge-small" leftIcon={false} />
                        ),
                }),
                actionsColumn,
            ],
            [checkboxColumn, actionsColumn, createdAtColumn],
        ),
        rowSelectionState: {
            rowSelection,
            setRowSelection,
        },
        paginationOptions: {
            defaultPageSize: 5,
            disableChangePageSize: true,
            disableGoToPage: true,
        },
        defaultSort: [
            {
                id: "created_at",
                desc: true,
            },
        ],
    });

    function onEdit(selectedLink) {
        setIsUpdateBulkLinkDialogOpen(true);
        setBulkUploadToUpdate(selectedLink);
    }

    return (
        <div>
            <Table table={table} noDataComponent="No bulk links available" />

            <UpdateBulkGeneratorDialog
                title="Edit bulk link"
                isOpen={isUpdateBulkLinkDialogOpen}
                bulkUploadToUpdate={bulkUploadToUpdate}
                onUploadUpdated={(updatedLink) => {
                    setBulkLinks(bulkLinks.map((link) => (link.id === updatedLink.id ? updatedLink : link)));

                    toast.success("Bulk upload updated");
                }}
                onClose={() => {
                    setIsUpdateBulkLinkDialogOpen(false);
                    setBulkUploadToUpdate(null);
                }}
                saveButtonText="Update"
            />
        </div>
    );
}

export default BulkLinks;
